import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Trash2, ChevronDown, List, Grid, CheckSquare, Square,
  ArrowDownAZ, ArrowUpAZ, ArrowDownWideNarrow, ArrowUpWideNarrow
} from "lucide-react";

export const API = '/api';

// ==================== VIEW PREFERENCES HOOK ====================
export const useViewPreferences = (storageKey) => {
  const [viewMode, setViewModeState] = useState(() => {
    try {
      return localStorage.getItem(`paladin_${storageKey}_viewMode`) || "list";
    } catch { return "grid"; }
  });
  
  const [sortMode, setSortModeState] = useState(() => {
    try {
      return localStorage.getItem(`paladin_${storageKey}_sortMode`) || "a-z";
    } catch { return "a-z"; }
  });

  const setViewMode = (mode) => {
    setViewModeState(mode);
    try { localStorage.setItem(`paladin_${storageKey}_viewMode`, mode); } catch {}
  };

  const setSortMode = (mode) => {
    setSortModeState(mode);
    try { localStorage.setItem(`paladin_${storageKey}_sortMode`, mode); } catch {}
  };

  return { viewMode, setViewMode, sortMode, setSortMode };
};

// ==================== VIEW CONTROLS COMPONENT ====================
export const ViewControls = ({ viewMode, setViewMode, sortMode, setSortMode }) => {
  const viewOptions = [
    { id: "grid", icon: Grid, label: "Grid" },
    { id: "list", icon: List, label: "List" },
  ];

  const sortOptions = [
    { id: "a-z", icon: ArrowDownAZ, label: "A-Z" },
    { id: "z-a", icon: ArrowUpAZ, label: "Z-A" },
    { id: "newest", icon: ArrowDownWideNarrow, label: "Newest" },
    { id: "oldest", icon: ArrowUpWideNarrow, label: "Oldest" },
  ];

  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-1 bg-[#0F1419] border border-[#1E293B] rounded-lg p-1">
        {viewOptions.map((option) => (
          <button
            key={option.id}
            onClick={() => setViewMode(option.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === option.id
                ? "bg-[#1E293B] text-white"
                : "text-gray-400 hover:text-white"
            }`}
            title={option.label}
            data-testid={`view-${option.id}`}
          >
            <option.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{option.label}</span>
          </button>
        ))}
      </div>

      <div className="relative">
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value)}
          className="appearance-none bg-[#0F1419] border border-[#1E293B] rounded-lg pl-3 pr-8 py-1.5 text-sm text-white cursor-pointer hover:border-blue-500/50 focus:border-primary outline-none"
          data-testid="sort-select"
        >
          {sortOptions.map((option) => (
            <option key={option.id} value={option.id}>
              Sort: {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
      </div>
    </div>
  );
};

// Helper function to sort items
export const sortItems = (items, sortMode, nameField = "name") => {
  const sorted = [...items];
  switch (sortMode) {
    case "a-z":
      return sorted.sort((a, b) => (a[nameField] || "").localeCompare(b[nameField] || ""));
    case "z-a":
      return sorted.sort((a, b) => (b[nameField] || "").localeCompare(a[nameField] || ""));
    case "newest":
      return sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    case "oldest":
      return sorted.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    default:
      return sorted;
  }
};

// ==================== BULK DELETE MODAL ====================
export const BulkDeleteModal = ({ items, itemType, nameField, onConfirm, onCancel, deleting }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    onClick={onCancel}
    data-testid="bulk-delete-modal"
  >
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      className="bg-[#141B2D] border border-[#1E293B] rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-6 border-b border-[#1E293B]">
        <h2 className="font-heading text-xl font-bold text-red-400 flex items-center gap-2">
          <Trash2 className="w-6 h-6" />
          Delete {items.length} {itemType}{items.length !== 1 ? 's' : ''}?
        </h2>
        <p className="text-gray-400 mt-2">
          This action cannot be undone. The following {itemType.toLowerCase()}s will be permanently deleted:
        </p>
      </div>
      
      <div className="p-6 max-h-[40vh] overflow-y-auto">
        <ul className="space-y-2">
          {items.map((item, i) => (
            <li key={i} className="flex items-center gap-3 bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2">
              <Trash2 className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span className="text-white truncate">{item[nameField] || item.title || item.name}</span>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="p-6 border-t border-[#1E293B] flex gap-4">
        <button
          onClick={onCancel}
          disabled={deleting}
          className="flex-1 bg-[#1E293B] text-white py-3 rounded-lg font-semibold hover:bg-[#2D3B4F] transition-colors disabled:opacity-50"
          data-testid="bulk-delete-cancel"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={deleting}
          className="flex-1 bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="bulk-delete-confirm"
        >
          {deleting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              Deleting...
            </>
          ) : (
            <>
              <Trash2 className="w-5 h-5" />
              Delete {items.length} {itemType}{items.length !== 1 ? 's' : ''}
            </>
          )}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

// ==================== SELECTION HEADER ====================
export const SelectionHeader = ({ selectedCount, totalCount, onSelectAll, onClearSelection, onDeleteSelected, allSelected }) => (
  <div className="flex items-center justify-between bg-[#0F1419] border border-[#1E293B] rounded-lg px-4 py-3 mb-4">
    <div className="flex items-center gap-4">
      <button
        onClick={onSelectAll}
        className="flex items-center gap-2 text-sm font-medium hover:text-blue-400 transition-colors"
        data-testid="select-all-btn"
      >
        {allSelected ? (
          <CheckSquare className="w-5 h-5 text-blue-400" />
        ) : (
          <Square className="w-5 h-5" />
        )}
        {allSelected ? 'Deselect All' : 'Select All'}
      </button>
      <span className="text-gray-500 text-sm">
        {selectedCount} of {totalCount} selected
      </span>
    </div>
    <div className="flex items-center gap-2">
      {selectedCount > 0 && (
        <>
          <button
            onClick={onClearSelection}
            className="text-sm text-gray-400 hover:text-white transition-colors px-3 py-1.5"
            data-testid="clear-selection-btn"
          >
            Clear
          </button>
          <button
            onClick={onDeleteSelected}
            className="flex items-center gap-2 bg-red-500/10 text-red-400 px-4 py-1.5 rounded-lg text-sm font-semibold hover:bg-red-500/20 transition-colors"
            data-testid="delete-selected-btn"
          >
            <Trash2 className="w-4 h-4" />
            Delete Selected ({selectedCount})
          </button>
        </>
      )}
    </div>
  </div>
);
